import React, { useState } from 'react';
import { useUiTheme } from 'framework/uiTheme';
import { useAsyncValue } from 'framework/utils';
import { useShowContext } from 'framework/show';
import { BlobPreviewModal, useBlobPreview } from 'framework/ui/Blob';
import { InputLoader } from 'framework/ui/mui-builder';
import { RESIZE_SIZES } from 'domain/shared/artifact';
import { useCompositeDocumentStorageContext } from './CompositeDocumentStorageContext';
import {
  ARTIFACTS_STORAGE_KEY,
  DOCUMENTS_STORAGE_KEY,
  DocumentIdentifier,
  useCompositeDocumentStorageService,
} from './useCompositeDocumentStorageService';

interface CompositeDocumentListFieldProps {
  artifactsListSource: string;
  documentsListsSource: string;
  EmptyComponent?: React.ReactNode;
}

const CompositeDocumentListField = ({
  artifactsListSource,
  documentsListsSource,
  EmptyComponent,
}: CompositeDocumentListFieldProps) => {
  const { AttachmentsListLayout, BaseFieldLayout } = useUiTheme();
  const { data } = useShowContext();
  const { isDocumentStorageAvailable } = useCompositeDocumentStorageContext();
  const { getDocumentsInfo, getDocumentThumbnail, downloadDocument } =
    useCompositeDocumentStorageService(isDocumentStorageAvailable);
  const { blobUrl, loading, setLoading, previewOpen, setPreviewOpen, closePreview, cleanup, setBlobUrl } =
    useBlobPreview();
  const [previewFileName, setPreviewFileName] = useState('');

  const handleClosePreview = () => {
    closePreview();
    cleanup();
  };

  const attachmentsInfo = useAsyncValue(() => {
    const artifactsList = (data?.getValueBySource(artifactsListSource) || []).map((item: string) => ({
      storage: ARTIFACTS_STORAGE_KEY,
      id: item,
    }));
    const documentsList = (data?.getValueBySource(documentsListsSource) || []).map((item: string) => ({
      storage: DOCUMENTS_STORAGE_KEY,
      id: item,
    }));

    return getDocumentsInfo([...artifactsList, ...documentsList]);
  }, [data]);

  const openPreview = async (id: DocumentIdentifier) => {
    setLoading(true);
    try {
      const previewUrl = await getDocumentThumbnail(id, RESIZE_SIZES.SIZE_1024);
      setBlobUrl(previewUrl);
      getDocumentsInfo([id]).then(info => {
        const documentInfo = info[0];
        setPreviewFileName(documentInfo?.title || 'preview');
      });
      setPreviewOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return attachmentsInfo ? (
    <>
      <AttachmentsListLayout
        attachments={attachmentsInfo}
        downloadFile={downloadDocument}
        getThumbnail={getDocumentThumbnail}
        openPreview={openPreview}
        isDownloadAvailable={true}
      />
      <BlobPreviewModal
        open={previewOpen}
        blobUrl={blobUrl}
        fileName={previewFileName}
        onClose={handleClosePreview}
        loading={loading}
      />
    </>
  ) : (
    EmptyComponent || <BaseFieldLayout value={<InputLoader />} />
  );
};

export default CompositeDocumentListField;
